import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, ChevronRight, Clock, TrendingUp, CheckCircle2, Bell } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { rebateAlertsAPI, vehicleActivityAPI } from '../../lib/api';
import VehicleDetailDialog from './VehicleDetailDialog';
import { fmtIncentive } from './specialsUtil';

const ts = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

/**
 * Rebate Alert list — Command Center READ surface (Phase D). Consumes the
 * /automotive/rebate-alerts feed (Phase B instances + Phase C notifications).
 * Read-only: NO authorization actions here (Phase E). Clicking a vehicle opens
 * the SHARED VehicleDetailDialog with integrated Specials context.
 */
export function RebateAlerts({ organizationId, locationIds, onChanged }) {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const [data, setData] = useState({ eligible: [], approaching: [], authorized: [], count: 0 });
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [convo, setConvo] = useState(null);

    const load = useCallback(async () => {
        if (!organizationId) return;
        setLoading(true);
        try {
            const r = await rebateAlertsAPI.list(organizationId, locationIds);
            setData(r.data || { eligible: [], approaching: [], authorized: [], count: 0 });
        } catch { setData({ eligible: [], approaching: [], authorized: [], count: 0 }); }
        setLoading(false);
    }, [organizationId, locationIds]);
    useEffect(() => { load(); }, [load]);

    const openVehicle = async (vid) => { const r = await vehicleActivityAPI.vehicleDetail(vid); setDetail(r.data); };
    const openConvo = async (cid) => { const r = await vehicleActivityAPI.conversation(cid); setConvo(r.data); };

    const empty = !loading && (data.eligible.length + data.approaching.length + data.authorized.length) === 0;

    const whenLabel = (row) => {
        if (row.state === 'approaching') return t('rebateAlert.approachingIn', { count: row.days_until });
        if (row.state === 'authorized') return t('rebateAlert.state.authorized');
        return t('rebateAlert.awaitingAuth');
    };
    const incentiveFor = (row) => fmtIncentive(row.current_incentive || row.proposed_incentive, t, lang);

    const Row = (row) => (
        <button key={row.vehicle_id} onClick={() => openVehicle(row.vehicle_id)}
            data-testid={`rebate-row-${row.stock_number}`} data-state={row.state}
            className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition-colors
                ${row.state === 'eligible' ? 'border-amber-500/50 bg-amber-500/5 hover:bg-amber-500/10'
                    : row.state === 'approaching' ? 'border-sky-500/30 bg-slate-800/40 hover:bg-slate-800/60'
                        : 'border-emerald-600/30 bg-slate-800/30 hover:bg-slate-800/50'}`}>
            {row.attention === 'unread' && <span className="mt-1 h-2 w-2 rounded-full bg-amber-400 shrink-0" data-testid="rebate-unread-dot" />}
            <Tag className={`w-5 h-5 shrink-0 ${row.state === 'eligible' ? 'text-amber-400' : row.state === 'approaching' ? 'text-sky-400' : 'text-emerald-400'}`} />
            <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-100 font-semibold truncate">
                    {[row.year, row.make, row.model].filter(Boolean).join(' ')} <span className="text-slate-400 font-normal">#{row.stock_number}</span>
                </div>
                <div className="text-[13px] text-slate-200 mt-0.5">
                    {incentiveFor(row)} <span className="text-slate-400">· {t('rebateAlert.thresholdDays', { n: row.threshold_days })}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-400 mt-0.5">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t('rebateAlert.daysInInventory', { n: row.days_in_inventory ?? 0 })}</span>
                    <span>· {whenLabel(row)}</span>
                    <span>· {t('rebateAlert.activity')}: {row.activity?.total ?? 0}</span>
                </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
        </button>
    );

    const Section = ({ icon, title, rows, testid }) => rows.length === 0 ? null : (
        <div className="space-y-2" data-testid={testid}>
            <p className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">{icon}{title} ({rows.length})</p>
            {rows.map(Row)}
        </div>
    );

    return (
        <div className="space-y-4" data-testid="rebate-alerts">
            <p className="text-sm text-slate-400">{t('rebateAlert.subtitle')}</p>
            {loading ? (
                <p className="text-sm text-slate-500" data-testid="rebate-loading">{t('rebateAlert.loading')}</p>
            ) : empty ? (
                <p className="text-sm text-slate-400" data-testid="rebate-empty">{t('rebateAlert.empty')}</p>
            ) : (
                <>
                    <Section icon={<Bell className="w-3.5 h-3.5 text-amber-400" />} title={t('rebateAlert.sectionEligible')} rows={data.eligible} testid="rebate-section-eligible" />
                    <Section icon={<TrendingUp className="w-3.5 h-3.5 text-sky-400" />} title={t('rebateAlert.sectionApproaching')} rows={data.approaching} testid="rebate-section-approaching" />
                    <Section icon={<CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />} title={t('rebateAlert.sectionAuthorized')} rows={data.authorized} testid="rebate-section-authorized" />
                </>
            )}

            <VehicleDetailDialog detail={detail} onClose={() => setDetail(null)} onOpenConvo={openConvo}
                onActionDone={async () => { await load(); await onChanged?.(); }} />

            <Dialog open={!!convo} onOpenChange={(o) => !o && setConvo(null)}>
                <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto bg-slate-950 border-slate-800 text-slate-100" data-testid="cc-conversation">
                    <DialogHeader><DialogTitle className="text-white">{convo?.request && `${convo.request.year} ${convo.request.make} ${convo.request.model} · #${convo.request.stock_number}`}</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        {(convo?.messages || []).map((m) => (
                            <div key={m.id} className={`p-2.5 rounded-lg text-sm ${m.sender_role === 'sales' ? 'bg-amber-500/10 ml-6' : 'bg-slate-800 mr-6'}`}>
                                <p className="text-[11px] text-slate-400 mb-0.5">{m.sender_name}{m.channel === 'sms' ? ` · SMS${m.sms_status ? ' · ' + m.sms_status : ''}` : ''} · {ts(m.created_at)}</p>
                                <p className="text-slate-100">{m.body}</p>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
